// SCR-STAFF-605 — قالب‌های چاپ (US-DOC-050، 051): ۱۴ قالب سیستمی با کاغذهای مجاز (A4، A5، حرارتی ۸۰ میلی‌متری)؛ شخصی‌سازی مجاز مستأجر: لوگو، رنگ،
// متن پاورقی، کاغذ پیش‌فرض و پنهان کردن بخش‌های غیرقانونی (بخش‌های قانونی قفل — BR-DOC-050؛ متن HTML قابل ویرایش نیست — C-AGENT-500) و
// پیش‌نمایش PDF با داده نمونه.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type DocPaperSize, type DocPrintTemplateView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, FormDialog, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { docKeys, SCR_STAFF_605 } from './common';
import { FileView } from './DocumentViewer';

function Preview({ template }: { template: DocPrintTemplateView }) {
  const { t } = useTranslation('doc');
  const { tenantApi } = useTenantSession();
  const [paper, setPaper] = useState<DocPaperSize>(template.paper);
  const preview = useMutation({
    mutationFn: async () => {
      const result = await tenantApi.POST('/api/v1/documents/print-templates/{code}:preview', {
        params: { path: { code: template.code } },
        body: { paper, format: 'Pdf' },
        parseAs: 'blob',
      });
      if (!result.response.ok) {
        throw (result.error as unknown) ?? new Error('preview');
      }
      return result.data as unknown as Blob;
    },
  });
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <TextField select size="small" label={t('doc:print.paper')} value={paper} onChange={(event) => setPaper(event.target.value as DocPaperSize)} sx={{ minWidth: 160 }} slotProps={{ htmlInput: { 'data-testid': 'print-preview-paper' } }}>
          {template.papers.map((value) => (
            <MenuItem key={String(value)} value={String(value)}>
              {t(`doc:paper.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={() => preview.mutate()} disabled={preview.isPending} data-testid="print-preview">
          {t('doc:print.preview')}
        </Button>
      </Stack>
      {preview.error ? <ErrorState error={preview.error} compact /> : null}
      {preview.data ? (
        <Stack data-testid="print-preview-ready">
          <FileView blob={preview.data} contentType="application/pdf" title={t(`doc:print_template.${template.code}`)} />
        </Stack>
      ) : null}
    </Stack>
  );
}

function EditorDialog({ template, canWrite, onClose }: { template: DocPrintTemplateView; canWrite: boolean; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [showLogo, setShowLogo] = useState(template.showLogo);
  const [color, setColor] = useState(template.primaryColor ?? '');
  const [footer, setFooter] = useState(template.footerText ?? '');
  const [paper, setPaper] = useState<DocPaperSize>(template.paper);
  const [hidden, setHidden] = useState<string[]>(template.sections.filter((section) => section.hidden).map((section) => section.key));
  const [dirty, setDirty] = useState(false);
  const save = useMutation({
    mutationFn: () => unwrap(tenantApi.PUT('/api/v1/documents/print-templates/{code}', { params: { path: { code: template.code } }, body: { showLogo, primaryColor: color || null, footerText: footer || null, paper, hiddenSections: hidden } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.printTemplates(session?.tenantId) });
      onClose();
    },
  });
  const touch = () => setDirty(true);
  return (
    <FormDialog open titleKey="doc:print.edit_title" screenId={SCR_STAFF_605} onSubmit={() => (canWrite ? save.mutate() : onClose())} onClose={onClose} isDirty={dirty} isSubmitting={save.isPending} submitError={save.error} maxWidth="lg">
      <Typography variant="subtitle1">{t(`doc:print_template.${template.code}`)}</Typography>
      <Alert severity="info">{t('doc:print.locked_hint')}</Alert>
      <FormControlLabel control={<Checkbox checked={showLogo} disabled={!canWrite} onChange={(event) => { touch(); setShowLogo(event.target.checked); }} />} label={t('doc:print.show_logo')} />
      <Stack direction="row" spacing={1}>
        <TextField label={t('doc:print.color')} value={color} disabled={!canWrite} onChange={(event) => { touch(); setColor(event.target.value); }} placeholder="#0B5CAD" slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'print-color' } }} />
        <TextField select label={t('doc:print.paper')} value={paper} disabled={!canWrite} onChange={(event) => { touch(); setPaper(event.target.value as DocPaperSize); }} sx={{ minWidth: 160 }}>
          {template.papers.map((value) => (
            <MenuItem key={String(value)} value={String(value)}>
              {t(`doc:paper.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <TextField label={t('doc:print.footer')} value={footer} disabled={!canWrite} onChange={(event) => { touch(); setFooter(event.target.value); }} slotProps={{ htmlInput: { maxLength: 300, 'data-testid': 'print-footer' } }} />
      <Typography variant="subtitle2">{t('doc:print.sections')}</Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
        {template.sections.map((section) => (
          <FormControlLabel
            key={section.key}
            disabled={section.isLegal || !canWrite}
            control={
              <Checkbox
                checked={!hidden.includes(section.key)}
                onChange={(event) => {
                  touch();
                  setHidden((current) => (event.target.checked ? current.filter((key) => key !== section.key) : [...current, section.key]));
                }}
                slotProps={{ input: { 'data-testid': `print-section-${section.key}` } as never }}
              />
            }
            label={`${t(`doc:print_section.${section.key}`, { defaultValue: section.key })}${section.isLegal ? ` (${t('doc:print.legal')})` : ''}`}
          />
        ))}
      </Stack>
      <Preview template={template} />
    </FormDialog>
  );
}

export function PrintTemplatesPage() {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('doc.print_template.manage');
  const [editing, setEditing] = useState<DocPrintTemplateView | null>(null);
  const templates = useQuery({ queryKey: docKeys.printTemplates(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/print-templates')) });
  const columns: DataTableColumn<DocPrintTemplateView>[] = [
    { id: 'name', headerKey: 'doc:print.columns.name', cell: (row) => t(`doc:print_template.${row.code}`) },
    { id: 'paper', headerKey: 'doc:print.columns.paper', cell: (row) => row.papers.map((paper) => t(`doc:paper.${String(paper)}`)).join(' · ') },
    { id: 'custom', headerKey: 'doc:print.columns.customized', cell: (row) => <span data-testid={`print-customized-${row.code}`}>{row.isCustomized ? t('doc:print.customized') : t('doc:print.system')}</span> },
    {
      id: 'actions',
      headerKey: 'doc:print.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setEditing(row)} data-testid={`print-edit-${row.code}`}>
          {write.canWrite ? t('doc:actions.edit') : t('doc:actions.preview')}
        </Button>
      ),
    },
  ];
  return (
    <ListPage screenId={SCR_STAFF_605} titleKey="doc:print.title" subtitleKey="doc:print.subtitle" filters={<WriteNotice write={write} />}>
      <DataTable captionKey="doc:print.title" columns={columns} rows={templates.data?.items} getRowId={(row) => row.code} isLoading={templates.isPending} error={templates.error} onRetry={() => void templates.refetch()} />
      {editing ? <EditorDialog template={editing} canWrite={write.canWrite} onClose={() => setEditing(null)} /> : null}
    </ListPage>
  );
}
